import type { Event } from '@unifyapps/defs/types/event';
import type { EmitEventType } from '@unifyapps/defs/hooks/useBlockEvents';
import { useCallback, useMemo } from 'react';
import type { BlockId } from '@unifyapps/defs/types/block';
import { NoCodeEventHandler } from '../../event';
import { useActionsContext } from '../../components/ActionsProvider';
import { useGetComputeContext } from '../computeContext';
import { useEvaluateConditionalOperator } from '../useComputeConditionEvaluationGetter';

function useBlockEvents(events?: Event[], blockId?: BlockId) {
  const { getComputeContext } = useGetComputeContext({
    mode: 'mutable',
  });
  const { evaluateConditionalOperator } = useEvaluateConditionalOperator();
  const { doAction } = useActionsContext();

  const { triggerEvent } = useMemo(() => {
    return new NoCodeEventHandler({
      getComputeContext,
      evaluateConditionalOperator,
      events: events || [],
    });
  }, [events, getComputeContext, evaluateConditionalOperator]);

  const emitEvent = useCallback<EmitEventType>(
    async ({ eventType, domEvent, actionContext, targetId }) => {
      if (doAction) {
        await triggerEvent({
          eventType,
          doAction,
          actionContext,
          domEvent,
          targetId,
          blockId,
        });
      }
    },
    [blockId, doAction, triggerEvent],
  );

  return {
    emitEvent,
  };
}

export default useBlockEvents;
